import React, { useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  Menu,
  Settings,
  Sparkles,
} from "lucide-react";

import { db } from "../firebase";
import { logoutUser } from "../services/authService";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import { usePanelTheme } from "../context/themeContext";

export default function Header({ title, onMenu, lang, setLang, t }) {

  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState({
    companyName: "",
    logoUrl: "",
  });

  const desktopMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navigate = useNavigate();
  const { currentTheme, theme } = usePanelTheme();

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "company"));

        if (snap.exists()) {
          const d = snap.data();

          setCompany({
            companyName: d.companyName || "",
            logoUrl: d.logoUrl || "",
          });
        }
      } catch (e) {
        console.error("company load error", e);
      }
    };

    loadCompany();
  }, []);

  useEffect(() => {

    const handleClickOutside = (event) => {

      const desktopClick =
        desktopMenuRef.current &&
        desktopMenuRef.current.contains(event.target);

      const mobileClick =
        mobileMenuRef.current &&
        mobileMenuRef.current.contains(event.target);

      if (!desktopClick && !mobileClick) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);

  }, []);

  const companyName = company.companyName || "BauHeld";

  const handleLogout = async () => {
    try {
      await logoutUser();
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <header className="sticky top-0 z-30 px-3 pt-3 md:px-6 md:pt-5 xl:px-8">

      <div
        className={`relative rounded-[24px] md:rounded-[28px] border shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur-xl ${currentTheme.header}`}
      >

        <div className="relative px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-5 lg:px-7">

          <div className="flex items-center justify-between gap-3">

            {/* LEFT */}
            <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">

              <button
                onClick={onMenu}
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm transition hover:-translate-y-0.5 md:hidden ${currentTheme.buttonSecondary}`}
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="min-w-0 flex-1">

                <div
                  className={`mb-1 hidden items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.22em] sm:inline-flex ${
                    theme === "dark"
                      ? "border-slate-700 bg-slate-900/70 text-slate-300"
                      : "border-slate-200 bg-white/80 text-slate-500"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  BauHeld Premium UI
                </div>

                <h1 className="truncate text-xl font-black leading-none tracking-tight sm:text-2xl md:text-[28px]">
                  {title}
                </h1>

                <p className={`mt-1 truncate text-xs sm:text-sm ${currentTheme.muted}`}>
                  {companyName}
                </p>

              </div>
            </div>

            {/* RIGHT DESKTOP */}
            <div className="hidden md:flex shrink-0 items-center gap-3">

              <ThemeSwitcher lang={lang} />
              <LanguageSwitcher lang={lang} setLang={setLang} />

              <div className="relative" ref={desktopMenuRef}>

                <button
                  onClick={() => setOpen(!open)}
                  className={`group flex items-center gap-2 rounded-[20px] border px-3 py-2 shadow-sm transition hover:-translate-y-0.5 ${
                    theme === "dark"
                      ? "border-slate-700 bg-slate-950/70"
                      : "border-white bg-white"
                  }`}
                >

                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white font-bold">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt="Company"
                        className="h-full w-full object-cover rounded-full"
                      />
                    ) : (
                      companyName.slice(0, 1).toUpperCase()
                    )}
                  </div>

                  <div className="hidden text-left lg:block">
                    <p className={`text-[11px] font-semibold uppercase ${currentTheme.muted}`}>
                      {t?.settings || "Settings"}
                    </p>
                    <p className="max-w-[150px] truncate text-sm font-bold">
                      {companyName}
                    </p>
                  </div>

                  <ChevronDown className={`h-4 w-4 ${open ? "rotate-180" : ""}`} />

                </button>

                {open && (

                  <div
                    className={`absolute right-0 mt-3 w-72 rounded-[24px] border shadow-2xl ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-950"
                        : "border-white bg-white"
                    }`}
                  >

                    <div className="border-b px-4 py-4">

                      <p className={`text-[11px] font-black uppercase ${currentTheme.muted}`}>
                        BauHeld
                      </p>

                      <p className="mt-1 text-sm font-bold truncate">
                        {companyName}
                      </p>

                    </div>

                    <button
                      onClick={() => {
                        navigate("/settings");
                        setOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold hover:bg-slate-50"
                    >
                      <Settings className="h-4 w-4" />
                      {t?.settings || "Settings"}
                    </button>

                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      {t?.logout || "Logout"}
                    </button>

                  </div>

                )}

              </div>
            </div>

            {/* MOBILE */}
            <div className="flex items-center gap-2 md:hidden">

              <ThemeSwitcher lang={lang} />
              <LanguageSwitcher lang={lang} setLang={setLang} />

              <div className="relative" ref={mobileMenuRef}>

                <button
                  onClick={() => setOpen(!open)}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white font-bold"
                >
                  {companyName.slice(0, 1).toUpperCase()}
                </button>

                {open && (

                  <div className="absolute right-0 mt-3 w-64 rounded-[24px] border bg-white shadow-2xl">

                    <button
                      onClick={() => {
                        navigate("/settings");
                        setOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                    >
                      <Settings className="h-4 w-4" />
                      {t?.settings || "Settings"}
                    </button>

                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      {t?.logout || "Logout"}
                    </button>

                  </div>

                )}

              </div>

            </div>

          </div>

        </div>

      </div>

    </header>
  );
}